import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useFormHook from '@shared/hooks/useFormHook';

const RegionMunicipalityModal = ({ onClose, onConfirm, type, editData = null, selectedCountry = null }) => {
    const { modalRef } = useFormHook({}, onClose);
    const { t } = useTranslation();

    const regionSchema = yup.object({
        name: yup.string()
            .required(t('validation.region.name.required'))
            .min(1, t('validation.region.name.minLength'))
            .max(120, t('validation.region.name.maxLength'))
            .test('description-length',t('validation.region.name.maxLength'), 
                function(value) {
                    if (!value) return true;
                    return value.length <= 100;
                }),
        transliteration: yup.string()
    });

    const municipalitySchema = yup.object({
        name: yup.string()
            .required(t('validation.subregion.name.required'))
            .min(1, t('validation.subregion.name.minLength'))
            .max(120, t('validation.subregion.name.maxLength'))
            .test('description-length', t('validation.subregion.name.maxLength'), 
                function(value) {
                    if (!value) return true;
                    return value.length <= 100;
                }),
        transliteration: yup.string()
    });

    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(type === 'region' ? regionSchema : municipalitySchema),
        defaultValues: {
            name: editData?.name || '',
            transliteration: editData?.transliteration || ''
        }
    });

    const isEdit = Boolean(editData);
    const title = isEdit 
        ? type === 'region' ? t('regions.modal.editRegion') : t('regions.modal.editMunicipality')
        : type === 'region' ? t('regions.modal.addRegion') : t('regions.modal.addMunicipality');

    const onSubmit = (data) => {
        const formData = type === 'region'
            ? {
                name: data.name,
                description: data.name.substring(0, 100),
                countryCode: selectedCountry?.value || editData?.countryCode,
                active: true
            }
            : {
                id: editData?.id,
                name: data.name,
                description: data.name.substring(0, 100),
                transliteration: data.transliteration,
                regionId: editData?.regionId,
                active: true
            };

        onConfirm(formData);
    };

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">{title}</h4>
                        <form id="entityForm" className="region-form" onSubmit={handleSubmit(onSubmit)}>
                            {type === 'region' ? (
                                <>
                                    <div className="form-group">
                                        <span className="span-bold">{t('regions.modal.name')} <span className="required">*</span></span>
                                        <input 
                                            type="text"
                                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                            placeholder={t('regions.modal.namePlaceholder')}
                                            {...register('name')}
                                        />
                                        {errors.name && (
                                            <div className="invalid-feedback">
                                                {errors.name.message}
                                            </div>
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('regions.modal.transliteration')}</span>
                                        <input 
                                            type="text"
                                            className={`form-control ${errors.transliteration ? 'is-invalid' : ''}`}
                                            placeholder={t('regions.modal.transliterationPlaceholder')}
                                            {...register('transliteration')}
                                        />
                                        {errors.transliteration && (
                                            <div className="invalid-feedback">
                                                {errors.transliteration.message}
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="form-group">
                                        <span className="span-bold">{t('regions.modal.municipalityName')} <span className="required">*</span></span>
                                        <input 
                                            type="text"
                                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                            placeholder={t('regions.modal.municipalityNamePlaceholder')}
                                            {...register('name')}
                                        />
                                        {errors.name && (
                                            <div className="invalid-feedback">
                                                {errors.name.message}
                                            </div>
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('regions.modal.transliteration')}</span>
                                        <input 
                                            type="text"
                                            className={`form-control ${errors.transliteration ? 'is-invalid' : ''}`}
                                            placeholder={t('regions.modal.municipalityTransliterationPlaceholder')}
                                            {...register('transliteration')}
                                        />
                                        {errors.transliteration && (
                                            <div className="invalid-feedback">
                                                {errors.transliteration.message}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </form>
                    </div>
                    <div className="modal-footer">
                        <button type="submit" form="entityForm" className="btn btn-primary">
                            {t('common.save')}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegionMunicipalityModal; 